import { useState, useCallback } from 'react';
import { Dropzone, IMAGE_MIME_TYPE } from '@mantine/dropzone';
import { IconUpload, IconPhoto, IconX, IconTrash } from '@tabler/icons-react';

interface DocumentUploadProps {
  label: string;
  description?: string;
  existingUrl?: string | null;
  onUpload: (file: File) => Promise<string | null>;
  onRemove?: () => void;
  disabled?: boolean;
}

export function DocumentUpload({
  label,
  description,
  existingUrl,
  onUpload,
  onRemove,
  disabled = false,
}: DocumentUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(existingUrl || null);
  const [error, setError] = useState<string | null>(null);

  const handleDrop = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;

      const file = files[0];
      setIsUploading(true);
      setError(null);

      try {
        // Create local preview immediately
        const localPreview = URL.createObjectURL(file);
        setPreviewUrl(localPreview);

        // Upload to server
        const uploadedUrl = await onUpload(file);

        if (uploadedUrl) {
          // Clean up local preview and use server URL
          URL.revokeObjectURL(localPreview);
          setPreviewUrl(uploadedUrl);
        } else {
          setError('Upload failed');
          setPreviewUrl(null);
        }
      } catch (err) {
        console.error('Upload error:', err);
        setError('Failed to upload file');
        setPreviewUrl(null);
      } finally {
        setIsUploading(false);
      }
    },
    [onUpload]
  );

  const handleRemove = useCallback(() => {
    setPreviewUrl(null);
    setError(null);
    onRemove?.();
  }, [onRemove]);

  // Show preview if we have an image
  if (previewUrl && !isUploading) {
    return (
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-300">{label}</label>
        <div className="relative group">
          <Dropzone
            onDrop={handleDrop}
            accept={IMAGE_MIME_TYPE}
            maxSize={10 * 1024 * 1024}
            maxFiles={1}
            disabled={disabled}
            styles={{
              root: {
                padding: 0,
                border: 'none',
                backgroundColor: 'transparent',
                cursor: disabled ? 'not-allowed' : 'pointer',
              },
            }}
          >
            <div className="card overflow-hidden relative">
              <img
                src={previewUrl}
                alt={label}
                className="w-full h-48 object-cover"
              />
              {/* Hover overlay with replace text */}
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <span className="text-white text-sm font-medium">Click to replace</span>
              </div>
            </div>
          </Dropzone>
          {/* Delete button */}
          <button
            onClick={handleRemove}
            className="absolute top-2 right-2 p-2 rounded-full bg-red-500/80 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 z-10"
            type="button"
            title="Remove image"
          >
            <IconTrash size={16} />
          </button>
          <p className="text-xs text-green-400 mt-1">Uploaded successfully</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-300">{label}</label>
      {description && (
        <p className="text-xs text-gray-500 mb-2">{description}</p>
      )}
      <Dropzone
        onDrop={handleDrop}
        accept={IMAGE_MIME_TYPE}
        maxSize={10 * 1024 * 1024} // 10MB
        maxFiles={1}
        disabled={disabled || isUploading}
        loading={isUploading}
        styles={{
          root: {
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            borderColor: error ? 'rgba(239, 68, 68, 0.5)' : 'rgba(75, 85, 99, 0.5)',
            borderWidth: '2px',
            borderStyle: 'dashed',
            borderRadius: '0.75rem',
            padding: '2rem',
            cursor: disabled || isUploading ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: 'rgba(0, 214, 245, 0.5)',
              backgroundColor: 'rgba(0, 214, 245, 0.05)',
            },
            '&[data-accept]': {
              borderColor: 'rgba(34, 197, 94, 0.5)',
              backgroundColor: 'rgba(34, 197, 94, 0.1)',
            },
            '&[data-reject]': {
              borderColor: 'rgba(239, 68, 68, 0.5)',
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
            },
          },
        }}
      >
        <div className="flex flex-col items-center justify-center gap-3 text-center">
          <Dropzone.Accept>
            <IconUpload size={40} className="text-green-400" stroke={1.5} />
          </Dropzone.Accept>
          <Dropzone.Reject>
            <IconX size={40} className="text-red-400" stroke={1.5} />
          </Dropzone.Reject>
          <Dropzone.Idle>
            <IconPhoto size={40} className="text-gray-500" stroke={1.5} />
          </Dropzone.Idle>

          <div>
            <p className="text-sm text-gray-300">
              {isUploading ? 'Uploading...' : 'Drop image here or click to upload'}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              PNG, JPG, or HEIC up to 10MB
            </p>
          </div>
        </div>
      </Dropzone>

      {error && <p className="text-xs text-red-400 mt-1">{error}</p>}
    </div>
  );
}
